import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { formatQuantity } from '@kronos/domain'
import { repository } from '../../data'
import type { ItemPage } from '../../data/inventory'
import { BuyLinks } from '../BuyLinks'
import { Icon } from '../icons'
import { EmptyState, Section } from '../primitives'
import { Link } from '../shell/Link'
import { StructureTree } from '../tree/StructureTree'
import { TreeIcon } from '../tree/treeIcons'
import { DeliveryTimeline } from './DeliveryTimeline'
import { Glyph } from './glyphs'
import { ItemEditor } from './ItemEditor'
import { CorrectCount } from './CorrectCount'
import { ItemGallery } from './ItemGallery'
import { ItemUnits } from './ItemUnits'
import { ShelfFigure, StockBadges, StockMeter } from './Stock'
import { trackingText, useCan } from './shared'

/**
 * One item: its pictures, how many we have, the way to receive more, what
 * arrived when, and every unit — each one a button to its own page. Engineers
 * also get Edit item, which opens the same `ItemEditor` as New item.
 */
export function ItemView({ id }: { id: string }): JSX.Element {
  const item = useQuery({ queryKey: ['inventory-item', id], queryFn: () => repository.item(id) })
  const can = useCan()
  const [editing, setEditing] = useState(false)
  const [recounting, setRecounting] = useState(false)

  if (item.isError) {
    return (
      <div className="page">
        <EmptyState
          icon="stock"
          title="No such item"
          action={
            <Link to={{ name: 'inventory' }} className="button">
              Back to Inventory
            </Link>
          }
        >
          It may have been retired.
        </EmptyState>
      </div>
    )
  }
  const it = item.data
  if (!it) return <div className="page" />

  const serialized = it.tracking === 'serialized'
  const mayReceive = it.receivable && can(serialized ? 'intake_component' : 'receive_stock')
  const mayEdit = can('edit_catalog')
  // Whoever could have got it right at the door, or an engineer.
  const mayRecount = it.receivable && (can('receive_stock') || mayEdit)

  return (
    <div className="page inv-item">
      <div>
        <Link to={{ name: 'inventory' }} className="button inv-back">
          <Glyph name="back" />
          Inventory
        </Link>
      </div>

      <div className="inv-hero">
        <ItemGallery itemId={it.id} name={it.name} category={it.category} />
        <div className="inv-hero__info">
          <span className="page-header__eyebrow">{it.category || 'No category'}</span>
          <h1 className="page-header__title">{it.name}</h1>
          <p className="page-header__subtitle">
            {[it.partNumber, it.supplier, trackingText(it.tracking, it.unit)].filter(Boolean).join(' · ')}
          </p>
          <StockBadges item={it} />
          <HeroNumbers it={it} />
          <div className="inv-hero__actions">
            {mayReceive ? (
              <Link to={{ name: 'receive', item: it.id }} className="button primary inv-hero__receive">
                <Icon name="receive" />
                Receive more
              </Link>
            ) : null}
            {mayRecount ? (
              <button type="button" className="inv-hero__edit" onClick={() => setRecounting(true)}>
                Correct count
              </button>
            ) : null}
            {mayEdit ? (
              <button type="button" className="inv-hero__edit" onClick={() => setEditing(true)}>
                <TreeIcon name="edit" />
                Edit item
              </button>
            ) : null}
            <BuyLinks links={it.purchaseLinks} />
          </div>
        </div>
      </div>

      {/* What it is made of, for the things we build here or have built — the
          same stock-aware tree as Design › How it's made, drawn from this item. */}
      {it.kind !== 'buy' ? (
        <Section
          label="Made of"
          actions={
            mayEdit ? (
              <button type="button" className="inv-section-button" onClick={() => setEditing(true)}>
                <TreeIcon name="edit" />
                Edit parts list
              </button>
            ) : undefined
          }
        >
          <StructureTree rootTypeId={it.id} compact />
        </Section>
      ) : null}

      <Section label="Deliveries">
        {it.receipts.length > 0 ? (
          <DeliveryTimeline receipts={it.receipts} />
        ) : (
          <div className="panel panel--padded inv-quiet muted">Nothing received through Receive yet.</div>
        )}
      </Section>

      {it.corrections.length > 0 ? (
        <Section label="Recounts">
          <div className="panel">
            <div className="row-list">
              {it.corrections.map((c) => (
                <div key={c.id} className="row-item">
                  <span className="row-item__main">
                    <span className="row-item__title">
                      {formatQuantity(c.was, serialized ? '' : it.unit)} → {formatQuantity(c.counted, serialized ? '' : it.unit)}
                    </span>
                    <span className="row-item__meta">
                      {new Date(c.at).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}
                      {c.by ? ` · ${c.by}` : ''}
                      {c.note ? ` · ${c.note}` : ''}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </Section>
      ) : null}

      {serialized ? (
        <Section label={`Units · ${it.unitCount}`}>
          {it.units.length > 0 ? (
            <ItemUnits units={it.units} marking={it.marking} total={it.unitCount} />
          ) : (
            <div className="panel panel--padded inv-quiet muted">None yet. Each one received gets a Kronos number and appears here.</div>
          )}
        </Section>
      ) : null}

      {recounting ? <CorrectCount item={it} onClose={() => setRecounting(false)} /> : null}
      {editing ? <ItemEditor typeId={it.id} onClose={() => setEditing(false)} onSaved={() => setEditing(false)} /> : null}
    </div>
  )
}

/** The numbers, large: on the shelf with its bar, then fitted, the depot and the minimum. */
function HeroNumbers({ it }: { it: ItemPage }): JSX.Element {
  const serialized = it.tracking === 'serialized'
  return (
    <div className="inv-numbers">
      <div className="inv-numbers__main">
        <ShelfFigure item={it} />
        <StockMeter item={it} />
      </div>
      <dl className="inv-numbers__rest">
        <div>
          <dt>{serialized ? 'Fitted' : 'Used in builds'}</dt>
          <dd className="figure">{formatQuantity(it.fitted, it.unit)}</dd>
        </div>
        {it.depot > 0 ? (
          <div>
            <dt>At the depot</dt>
            <dd className="figure">{it.depot}</dd>
          </div>
        ) : null}
        <div>
          <dt>Minimum</dt>
          <dd className={it.safetyStock ? 'figure' : 'muted'}>{it.safetyStock ? formatQuantity(it.safetyStock, it.unit) : 'Not set'}</dd>
        </div>
      </dl>
    </div>
  )
}
